import { ESocketEvents as ERpSocketEvents, TBoxBodyPublic, TGamePublic, TSessionBodyPublic } from '@lbs/shared-rp-types'
import { observer } from 'mobx-react-lite'
import { createContext, FC, ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import { io, Socket } from 'socket.io-client'
import { useStore } from 'stores'
import { useRussianPokerStore } from '../useRussianPokerStore'

// Socket Context
interface SocketContextType {
  socket: Socket | null
  isConnected: boolean
  emit: (event: string, ...args: any[]) => void
}

const SocketContext = createContext<SocketContextType | undefined>(undefined)

export const useSocketContext = () => {
  const context = useContext(SocketContext)
  if (!context) {
    throw new Error('useSocketContext must be used within SocketProvider')
  }
  return context
}

interface SocketProviderProps {
  children: ReactNode
}

export const SocketProvider: FC<SocketProviderProps> = observer(({ children }) => {
  const { loginStore } = useStore()
  const {
    gameId,
    setGame,
    setSession,
    setBoxes,
    setOnlineStatusPlayer,
    onPlayerStatusChange,
    pathGame,
    pathSession,
    pathBoxes,
    eventBox,
  } = useRussianPokerStore()
  const [socket, setSocket] = useState<Socket | null>(null)
  const [isConnected, setIsConnected] = useState(false)

  useEffect(() => {
    const newSocket = io(`${process.env.REACT_APP_PRP_2BOX_API}/prp2b_dealer`, {
      query: {
        dealerId: loginStore.operator?.id || '',
      },
      transports: ['websocket'],
      forceNew: true,
    })

    newSocket.on(ERpSocketEvents.CONNECT, () => {
      setIsConnected(true)
      newSocket.emit(
        ERpSocketEvents.JOIN_GAME,
        gameId,
        (response: {
          success: boolean
          game: TGamePublic | null
          session: TSessionBodyPublic | null
          boxes: TBoxBodyPublic[]
          isOnlinePlayer: boolean
          error?: string
        }) => {
          if (response.success) {
            if (response.game) setGame(response.game)
            if (response.session) setSession(response.session)
            if (response.boxes) setBoxes(response.boxes)
            setOnlineStatusPlayer(response.isOnlinePlayer)
          }
        }
      )
    })

    newSocket.on(ERpSocketEvents.PLAYER_STATUS_CHANGED, onPlayerStatusChange)
    newSocket.on(ERpSocketEvents.GAME, pathGame)
    newSocket.on(ERpSocketEvents.SESSION, pathSession)
    newSocket.on(ERpSocketEvents.BOX, pathBoxes)
    newSocket.on(ERpSocketEvents.EVENT_BOX, eventBox)

    newSocket.on(ERpSocketEvents.DISCONNECT, () => {
      setIsConnected(false)
    })

    setSocket(newSocket)

    return () => {
      newSocket.disconnect()
      newSocket.removeAllListeners()
    }
  }, [
    gameId,
    loginStore.operator?.id,
    pathGame,
    pathSession,
    pathBoxes,
    eventBox,
    setGame,
    setSession,
    setBoxes,
    setOnlineStatusPlayer,
    onPlayerStatusChange,
  ])

  const emit = useMemo(
    () =>
      (event: string, ...args: any[]) => {
        if (socket && isConnected) {
          socket.emit(event, ...args)
        }
      },
    [socket, isConnected]
  )

  const contextValue = useMemo(() => ({ socket, isConnected, emit }), [socket, isConnected, emit])

  return <SocketContext.Provider value={contextValue}>{children}</SocketContext.Provider>
})
